import { useEffect, useState } from 'react';
import { apiFetch, readJsonResponse } from '../api/client';

function formatCommentWhen(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Resolve controller/device id for tracking comments API. */
export function resolveCommentDeviceId(riskCase) {
  if (!riskCase) {
    return null;
  }

  const casePathId = riskCase.instance_path?.controller_id;
  if (casePathId != null && casePathId !== '') {
    return casePathId;
  }

  const hits = Array.isArray(riskCase.hits) ? riskCase.hits : [];

  for (const hit of hits) {
    const pathId = hit?.instance_path?.controller_id;
    if (pathId != null && pathId !== '') {
      return pathId;
    }
  }

  const risk1 = hits.find((hit) => Number(hit?.risk_number) === 1);
  if (risk1?.facts?.device_id != null && risk1.facts.device_id !== '') {
    return risk1.facts.device_id;
  }

  for (const hit of hits) {
    const facts = hit?.facts || {};
    if (facts.device_id != null && facts.device_id !== '') {
      return facts.device_id;
    }
    if (facts.controller_id != null && facts.controller_id !== '') {
      return facts.controller_id;
    }
  }

  if (riskCase.controller_id != null && riskCase.controller_id !== '') {
    return riskCase.controller_id;
  }
  if (riskCase.device_id != null && riskCase.device_id !== '') {
    return riskCase.device_id;
  }

  return null;
}

function formatCommentAuthor(comment) {
  return comment?.created_by || comment?.updated_by || '—';
}

function commentsNewestFirst(comments) {
  return [...comments].sort((left, right) => {
    const leftTime = Date.parse(left.created_at) || 0;
    const rightTime = Date.parse(right.created_at) || 0;
    return rightTime - leftTime;
  });
}

export function commentDeviceLabel(riskCase) {
  if (!riskCase) {
    return '—';
  }

  const serial =
    riskCase.header?.controller_serial ||
    riskCase.hits?.find((hit) => hit?.facts?.serial_number)?.facts?.serial_number ||
    riskCase.hits?.find((hit) => hit?.facts?.controller_serial_number)?.facts?.controller_serial_number;

  if (serial) {
    return serial;
  }

  const deviceId = resolveCommentDeviceId(riskCase);
  return deviceId != null ? `device_id ${deviceId}` : '—';
}

export default function DeviceCommentsPanel({ deviceId, serialNumber, onClose }) {
  const [comments, setComments] = useState([]);
  const [hotWaterUnused, setHotWaterUnused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [newCommentDraft, setNewCommentDraft] = useState('');
  const [authorDraft, setAuthorDraft] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');

  useEffect(() => {
    if (deviceId == null || deviceId === '') {
      setComments([]);
      setHotWaterUnused(false);
      setLoading(false);
      setError('Нет device_id для комментариев');
      return undefined;
    }

    let cancelled = false;

    async function loadComments() {
      setLoading(true);
      setError('');
      setMessage('');

      try {
        const response = await apiFetch(`/api/devices/${deviceId}/tracking`);
        const payload = await readJsonResponse(response, 'Комментарии контроллера');

        if (!response.ok) {
          throw new Error(payload.message || `Ошибка ${response.status}`);
        }

        if (!cancelled) {
          setComments(Array.isArray(payload.comments) ? payload.comments : []);
          setHotWaterUnused(Boolean(payload.hot_water_unused));
          setEditingCommentId(null);
          setEditingDraft('');
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Не удалось загрузить комментарии');
          setComments([]);
          setHotWaterUnused(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadComments();
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  function applyPayload(payload, successMessage) {
    setComments(Array.isArray(payload.comments) ? payload.comments : []);
    if (typeof payload.hot_water_unused === 'boolean') {
      setHotWaterUnused(payload.hot_water_unused);
    }
    setMessage(successMessage);
  }

  async function handleToggleHotWaterUnused() {
    if (deviceId == null) {
      return;
    }

    const nextValue = !hotWaterUnused;
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await apiFetch(`/api/devices/${deviceId}/tracking`, {
        method: 'PUT',
        body: JSON.stringify({ hot_water_unused: nextValue }),
      });
      const payload = await readJsonResponse(response, 'Флаг ГВС');

      if (!response.ok) {
        throw new Error(payload.message || `Ошибка ${response.status}`);
      }

      applyPayload(
        payload,
        nextValue ? 'Отмечено: горячую воду не использует' : 'Флаг ГВС снят',
      );
    } catch (toggleError) {
      setError(toggleError.message || 'Не удалось сохранить флаг ГВС');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddComment() {
    const text = newCommentDraft.trim();
    const author = authorDraft.trim();
    if (!text || !author || deviceId == null) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await apiFetch(`/api/devices/${deviceId}/tracking/comments`, {
        method: 'POST',
        body: JSON.stringify({ text, author }),
      });
      const payload = await readJsonResponse(response, 'Добавление комментария');

      if (!response.ok) {
        throw new Error(payload.message || `Ошибка ${response.status}`);
      }

      applyPayload(payload, 'Комментарий добавлен');
      setNewCommentDraft('');
    } catch (saveError) {
      setError(saveError.message || 'Не удалось добавить комментарий');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveEditedComment(commentId) {
    const text = editingDraft.trim();
    if (!text || deviceId == null) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await apiFetch(`/api/devices/${deviceId}/tracking/comments/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ text }),
      });
      const payload = await readJsonResponse(response, 'Редактирование комментария');

      if (!response.ok) {
        throw new Error(payload.message || `Ошибка ${response.status}`);
      }

      applyPayload(payload, 'Комментарий обновлён');
      setEditingCommentId(null);
      setEditingDraft('');
    } catch (saveError) {
      setError(saveError.message || 'Не удалось сохранить комментарий');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteComment(commentId) {
    if (deviceId == null) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await apiFetch(`/api/devices/${deviceId}/tracking/comments/${commentId}`, {
        method: 'DELETE',
      });
      const payload = await readJsonResponse(response, 'Удаление комментария');

      if (!response.ok) {
        throw new Error(payload.message || `Ошибка ${response.status}`);
      }

      applyPayload(payload, 'Комментарий удалён');
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditingDraft('');
      }
    } catch (deleteError) {
      setError(deleteError.message || 'Не удалось удалить комментарий');
    } finally {
      setSaving(false);
    }
  }

  const title = serialNumber || (deviceId != null ? `id ${deviceId}` : '—');

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-950/80">
      <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 dark:border-slate-800">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</span>
            {deviceId != null && (
              <span className="shrink-0 text-[11px] tabular-nums text-slate-400">#{deviceId}</span>
            )}
            {!loading && (
              <span className="shrink-0 text-[11px] text-slate-400">
                {comments.length}{' '}
                {comments.length === 1 ? 'комментарий' : comments.length < 5 ? 'комментария' : 'комментариев'}
              </span>
            )}
          </div>
        </div>
        <label className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600 dark:border-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={hotWaterUnused}
            disabled={loading || saving || deviceId == null}
            onChange={handleToggleHotWaterUnused}
            className="h-3 w-3"
            title="Горячую воду не использует"
          />
          ГВС выкл
        </label>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Скрыть"
            title="Скрыть"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            ×
          </button>
        ) : null}
      </div>

      <div className="max-h-52 overflow-y-auto px-2.5 py-1.5">
        {loading ? (
          <p className="py-2 text-xs text-slate-500">Загрузка…</p>
        ) : comments.length === 0 ? (
          <p className="py-2 text-xs text-slate-500">Пока нет комментариев</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800/80">
            {commentsNewestFirst(comments).map((comment) => {
              const isEditing = editingCommentId === comment.id;

              return (
                <li key={comment.id} className="group py-1.5">
                  <div className="mb-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
                    <span className="tabular-nums">{formatCommentWhen(comment.created_at)}</span>
                    <span className="font-medium text-slate-500 dark:text-slate-400">
                      {formatCommentAuthor(comment)}
                    </span>
                    {comment.updated_at && comment.updated_at !== comment.created_at ? (
                      <span className="text-slate-400">изм.</span>
                    ) : null}
                    <span className="ml-auto flex gap-1 opacity-70 transition group-hover:opacity-100">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            disabled={saving || !editingDraft.trim()}
                            onClick={() => handleSaveEditedComment(comment.id)}
                            className="rounded px-1.5 py-0.5 text-[11px] text-sky-700 hover:bg-sky-50 disabled:opacity-40 dark:text-sky-300 dark:hover:bg-sky-950/40"
                          >
                            OK
                          </button>
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditingDraft('');
                            }}
                            className="rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            Отмена
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => {
                            setEditingCommentId(comment.id);
                            setEditingDraft(comment.text);
                          }}
                          className="rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          Изм.
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleDeleteComment(comment.id)}
                        className="rounded px-1.5 py-0.5 text-[11px] text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/30"
                      >
                        Удал.
                      </button>
                    </span>
                  </div>
                  {isEditing ? (
                    <textarea
                      rows={2}
                      value={editingDraft}
                      disabled={saving}
                      onChange={(event) => setEditingDraft(event.target.value)}
                      className="mt-1 w-full resize-y rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 outline-none focus:border-sky-400/60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-xs leading-snug text-slate-800 dark:text-slate-200">
                      {comment.text}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-slate-100 px-2.5 py-1.5 dark:border-slate-800">
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-end">
          <input
            id={`device-comments-author-${deviceId}`}
            type="text"
            value={authorDraft}
            disabled={loading || saving || deviceId == null}
            onChange={(event) => setAuthorDraft(event.target.value)}
            placeholder="Автор"
            className="w-full shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 outline-none focus:border-sky-400/60 disabled:opacity-60 sm:w-28 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <textarea
            id={`device-comments-new-${deviceId}`}
            rows={1}
            value={newCommentDraft}
            disabled={loading || saving || deviceId == null}
            onChange={(event) => setNewCommentDraft(event.target.value)}
            placeholder="Новый комментарий…"
            className="min-h-[1.75rem] w-full flex-1 resize-y rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 outline-none focus:border-sky-400/60 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <button
            type="button"
            disabled={loading || saving || deviceId == null || !newCommentDraft.trim() || !authorDraft.trim()}
            onClick={handleAddComment}
            className="shrink-0 rounded-md border border-sky-400/40 bg-sky-400/10 px-2.5 py-1 text-xs font-medium text-sky-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-sky-200"
          >
            {saving ? '…' : 'Добавить'}
          </button>
        </div>
        {(message || error) && (
          <p className={`mt-1 text-[11px] ${error ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}`}>
            {error || message}
          </p>
        )}
      </div>
    </div>
  );
}
